import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CheckCircle2, Circle, Clock, History, FileText, Hourglass, Unlock, Edit3 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface HistoryItem {
    dateTime: string;
    changes: Array<{ field: string; from?: any; to?: any }>;
    actorEmail?: string;
}

// New interface for a unified timeline event
interface TimelineEvent {
    type: 'creation' | 'statusChange' | 'detailingSubmission' | 'waitingDetailing' | 'reopening' | 'draft' | 'gestorDispatch';
    date: string;
    description: string;
    actor?: string;
    fromStatus?: string; // Only for statusChange type
    toStatus?: string;   // Only for statusChange type
    dateUnknown?: boolean;
    note?: string; // For gestor dispatch notes
}

interface AmendmentTimelineProps {
    history: HistoryItem[];
    createdAt?: string;
    className?: string;
    detailingSaved?: boolean;
    hasDetail?: boolean;
    currentStatus?: string;
    dispatchNote?: string;
}

export function AmendmentTimeline({ history, createdAt, className, detailingSaved, hasDetail, currentStatus, dispatchNote }: AmendmentTimelineProps) {
    const allTimelineEvents: TimelineEvent[] = [];

    // 1. Add creation event
    if (createdAt) {
        allTimelineEvents.push({
            type: 'creation',
            date: createdAt,
            description: 'Emenda Criada',
        });
    }

    // 2. Process history items
    history.forEach(item => {
        const statusChange = item.changes.find(c => c.field === 'status');
        const federalStatusChange = item.changes.find(c => c.field === 'federalStatus');
        const detailingSavedChange = item.changes.find(c => c.field === 'detailingSaved' && c.to === true);
        const detailingReopenedChange = item.changes.find(c => c.field === 'detailingSaved' && c.to === false);
        const dispatchAction = item.changes.find(c => c.field === 'action');
        const dispatchStatusClear = item.changes.find(c => c.field === 'dispatchStatus' && c.from === 'retificar');

        // Check if this is a gestor dispatch action
        if (dispatchAction && dispatchAction.to) {
            let label = dispatchAction.to.split(':')[0];

            // Map legacy/simple labels to full status labels
            if (label === 'Aprovação') label = 'Aprovado pelo Gestor';
            if (label === 'Reprovação') label = 'Reprovada pelo Gestor';
            if (label === 'Devolução para Retificação') label = 'Devolvida para Retificação';

            allTimelineEvents.push({
                type: 'gestorDispatch',
                date: item.dateTime,
                description: label,
                actor: item.actorEmail,
                note: dispatchAction.to.includes(':') ? dispatchAction.to.split(':').slice(1).join(':').trim() : undefined
            });
        }

        // Prioritize detailing submission event over status change if both happen
        if (dispatchStatusClear) {
            allTimelineEvents.push({
                type: 'detailingSubmission',
                date: item.dateTime,
                description: 'Correção realizada e enviada para reanálise',
                actor: item.actorEmail,
            });
        } else if (detailingSavedChange) {
            allTimelineEvents.push({
                type: 'detailingSubmission',
                date: item.dateTime,
                description: 'Formulário de detalhamento enviado',
                actor: item.actorEmail,
            });
        } else if (statusChange && (!dispatchAction || !dispatchAction.to)) {
            // Only add status change if it's not part of a valid gestor dispatch
            // OR if the dispatch action is somehow missing/malformed but status changed
            allTimelineEvents.push({
                type: 'statusChange',
                date: item.dateTime,
                description: `Status alterado para ${statusChange.to}`,
                actor: item.actorEmail,
                fromStatus: statusChange.from,
                toStatus: statusChange.to,
            });
        }

        if (federalStatusChange) {
            allTimelineEvents.push({
                type: 'statusChange',
                date: item.dateTime,
                description: `Situação Federal alterada`,
                actor: item.actorEmail,
                fromStatus: federalStatusChange.from,
                toStatus: federalStatusChange.to,
            });
        }

        if (detailingReopenedChange) {
            allTimelineEvents.push({
                type: 'reopening',
                date: item.dateTime,
                description: 'Formulário reaberto para correções',
                actor: item.actorEmail,
            });
        }
    });

    // 2.5 Fallback for missing detailing event
    // If the amendment is marked as saved/locked but no corresponding history event exists (e.g. log failure),
    // inject a synthetic event so it appears in the timeline.
    if (detailingSaved) {
        const hasDetailingEvent = allTimelineEvents.some(e => e.type === 'detailingSubmission');
        if (!hasDetailingEvent) {
            // Use current date to ensure it appears at the top (most recent)
            // since we know it is currently saved/locked.
            allTimelineEvents.push({
                type: 'detailingSubmission',
                date: new Date().toISOString(),
                description: 'Formulário de detalhamento enviado',
                dateUnknown: true
            });
        }
    } else if (hasDetail) {
        // Is draft
        allTimelineEvents.push({
            type: 'draft',
            date: new Date().toISOString(),
            description: 'Rascunho salvo (em andamento)',
            dateUnknown: true
        });
    }

    // 2.6 Fallback for missing Status/Dispatch event
    // If we have a finalized status like "Aprovada pelo Gestor" but no event in history, synthetic add it.
    if (currentStatus && (
        currentStatus.toLowerCase().includes('aprovada') ||
        currentStatus.toLowerCase().includes('reprovada') ||
        currentStatus.toLowerCase().includes('retificação')
    )) {
        // Check if we already have a dispatch event matching this loosely
        const hasDispatchEvent = allTimelineEvents.some(e =>
            e.type === 'gestorDispatch' ||
            (e.type === 'statusChange' && e.toStatus === currentStatus) ||
            (e.type === 'reopening' && currentStatus.includes('retificação')) // reopening usually implies rectification
        );

        if (!hasDispatchEvent) {
            allTimelineEvents.push({
                type: 'gestorDispatch',
                date: new Date().toISOString(), // We don't know the date, so we assume "now" or flag as unknown
                description: currentStatus,
                dateUnknown: true,
                note: dispatchNote
            });
        }
    }

    // 3. Add "Waiting for Detailing" event
    // The user requested this status to appear "since 01/12" for all amendments.
    // It should remain in the timeline even after detailing is submitted,
    // UNLESS the detailing was submitted BEFORE the waiting period started.

    const baseWaitingDate = new Date('2025-12-01T08:00:00');
    // If created after Dec 1st, the waiting starts at creation.
    const effectiveWaitingDate = (createdAt && new Date(createdAt) > baseWaitingDate)
        ? new Date(createdAt)
        : baseWaitingDate;

    // Check if it was already sent before the effective waiting date
    const sentEvent = allTimelineEvents.find(e => e.type === 'detailingSubmission');
    const sentBeforeWaiting = sentEvent && new Date(sentEvent.date) < effectiveWaitingDate;

    if (!sentBeforeWaiting) {
        allTimelineEvents.push({
            type: 'waitingDetailing',
            date: effectiveWaitingDate.toISOString(),
            description: 'Aguardando Detalhamento',
        });
    }

    // 4. Sort all events by date (newest first for timeline display)
    // If dates are identical, we might want stable sorting. Javascript sort is usually stable.
    allTimelineEvents.sort((a, b) => {
        const diff = new Date(b.date).getTime() - new Date(a.date).getTime();
        if (diff !== 0) return diff;
        // Tie-breaker: gestorDispatch should be top if same date (most important)
        if (a.type === 'gestorDispatch' && b.type !== 'gestorDispatch') return -1;
        if (b.type === 'gestorDispatch' && a.type !== 'gestorDispatch') return 1;

        // Tie-breaker: detailingSubmission and draft should be "newer"/top if same date (e.g. synthetic)
        if ((a.type === 'detailingSubmission' || a.type === 'draft') && !(b.type === 'detailingSubmission' || b.type === 'draft')) return -1;
        if ((b.type === 'detailingSubmission' || b.type === 'draft') && !(a.type === 'detailingSubmission' || a.type === 'draft')) return 1;
        return 0;
    });

    const formatActor = (actor?: string) => {
        if (!actor) return '';
        const match = actor.match(/^cnpj-(\d+)@/);
        if (match && match[1]) {
            return match[1].replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
        }
        return actor;
    };

    if (allTimelineEvents.length === 0) {
        return (
            <div className="text-center py-8 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>Nenhum histórico registrado para esta emenda.</p>
            </div>
        );
    }

    return (
        <div className={cn("relative pl-4", className)}>
            <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-slate-200" />

            <div className="space-y-8">
                {allTimelineEvents.map((event, index) => (
                    <div key={index} className="relative pl-8">
                        {/* Dot on the line */}
                        <span className="absolute left-0 top-1.5 -translate-x-1/2 flex h-4 w-4 items-center justify-center rounded-full bg-white ring-4 ring-white">
                            {event.type === 'creation' && <Clock className="h-2.5 w-2.5 text-slate-400" />}
                            {event.type === 'statusChange' && <Circle className="h-2.5 w-2.5 text-blue-600 fill-blue-600" />}
                            {event.type === 'detailingSubmission' && <FileText className="h-2.5 w-2.5 text-emerald-600" />}
                            {event.type === 'waitingDetailing' && <Hourglass className="h-2.5 w-2.5 text-amber-500" />}
                            {event.type === 'reopening' && <Unlock className="h-2.5 w-2.5 text-orange-600" />}
                            {event.type === 'draft' && <Edit3 className="h-2.5 w-2.5 text-blue-500" />}
                            {event.type === 'gestorDispatch' && <CheckCircle2 className="h-2.5 w-2.5 text-purple-600" />}
                        </span>

                        <div className="flex flex-col gap-1">
                            <h4 className={cn(
                                "text-sm font-semibold leading-tight",
                                event.type === 'creation' && "text-slate-700",
                                event.type === 'statusChange' && "text-blue-700",
                                event.type === 'detailingSubmission' && "text-emerald-700",
                                event.type === 'waitingDetailing' && "text-amber-700",
                                event.type === 'reopening' && "text-orange-700",
                                event.type === 'draft' && "text-blue-600",
                                event.type === 'gestorDispatch' && "text-purple-700"
                            )}>
                                {event.description}
                            </h4>
                            <span className="text-[10px] sm:text-xs text-slate-400 font-medium">
                                {event.dateUnknown
                                    ? "Data não registrada"
                                    : format(new Date(event.date), "d 'de' MMM 'de' yyyy, HH:mm", { locale: ptBR })}
                            </span>
                        </div>

                        {event.type === 'statusChange' && event.fromStatus && (
                            <p className="text-sm text-slate-500 mt-1">
                                Alterado de <span className="font-medium text-slate-700">{event.fromStatus}</span> para <span className="font-medium text-blue-700">{event.toStatus}</span>
                            </p>
                        )}

                        {event.type === 'waitingDetailing' && (
                            <p className="text-sm text-slate-500 mt-1">
                                Pendente de preenchimento pela instituição.
                            </p>
                        )}

                        {event.type === 'draft' && (
                            <p className="text-sm text-slate-500 mt-1">
                                O preenchimento foi iniciado mas ainda não foi finalizado.
                            </p>
                        )}

                        {event.type === 'gestorDispatch' && event.note && (
                            <div className="mt-2 p-3 bg-purple-50 border border-purple-200 rounded-md">
                                <p className="text-xs font-semibold text-purple-900 mb-1">Parecer do Gestor:</p>
                                <p className="text-sm text-purple-800">{event.note}</p>
                            </div>
                        )}

                        {event.actor && (
                            <p className="text-xs text-slate-400 mt-1">
                                Por: {formatActor(event.actor)}
                            </p>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
